import axios from 'axios';

const MBTA_CALLOUT_API_URL = 'https://activities.marriott.com/api/services/bonvoy_app/products';

const MAX_EXPERIENCES = 10;
const PRIMARY_CATEGORY = 'Our Favorites';
const FALLBACK_CATEGORY = 'Marriott Bonvoy Premier Experiences';
const FURTHER_AWAY_CATEGORY = 'Further Away';
const FURTHER_AWAY_MIN_RATING = 4.0;

const mbtaResponseCache = new Map<string, unknown[]>();
const mbtaInFlightCache = new Map<string, Promise<unknown[]>>();

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getRequestKey = (requestData: any): string => {
  return [
    requestData?.code || '',
    requestData?.currency || '',
    requestData?.scope || '',
    requestData?.from || '',
    requestData?.to || '',
  ].join('|');
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const fetchAndNormalizeMBTAData = async (requestData: any): Promise<unknown[]> => {
  const requestKey = getRequestKey(requestData);

  if (mbtaResponseCache.has(requestKey)) {
    return mbtaResponseCache.get(requestKey) || [];
  }

  if (mbtaInFlightCache.has(requestKey)) {
    return mbtaInFlightCache.get(requestKey) || Promise.resolve([]);
  }

  const uri = `${MBTA_CALLOUT_API_URL}?code=${requestData.code}&currency=${requestData.currency}&scope=${requestData.scope}&from=${requestData.from}&to=${requestData.to}`;

  const requestPromise = axios
    .get(uri)
    .then(({ data }) => {
      const normalizedData = normalizeMBTAResponse(data);
      const response = getFilteredExperiences(normalizedData);
      mbtaResponseCache.set(requestKey, response);
      return response;
    })
    .catch(error => {
      console.error('fetchMBTAcalloutData Error Data Message', error);
      return [];
    })
    .finally(() => {
      mbtaInFlightCache.delete(requestKey);
    });

  mbtaInFlightCache.set(requestKey, requestPromise);
  return requestPromise;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const fetchMBTAcalloutData = async (requestData: any): Promise<any> => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const errorData: any = {
    url: MBTA_CALLOUT_API_URL,
    show: true,
    status: '400',
    products: {},
  };

  if (!requestData?.code || !requestData?.currency || !requestData?.scope) {
    return errorData;
  }

  const response = await fetchAndNormalizeMBTAData(requestData);
  return { response };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const prefetchMBTAcalloutData = async (requestData: any): Promise<void> => {
  if (!requestData?.code || !requestData?.currency || !requestData?.scope) {
    return;
  }

  await fetchAndNormalizeMBTAData(requestData);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getProductKey = (p: any): string => p.name || p.title || JSON.stringify(p);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getFilteredExperiences = (data: any[]): any[] => {
  if (!Array.isArray(data)) {
    return [];
  }

  const primaryProducts = sortByRating(getCategoryProducts(PRIMARY_CATEGORY, data));
  let combined = [...primaryProducts];

  if (combined.length < MAX_EXPERIENCES) {
    const fallbackProducts = getCategoryProducts(FALLBACK_CATEGORY, data);
    const seenIds = new Set(combined.map(getProductKey));
    const uniqueFallback = sortByRating(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      fallbackProducts.filter((p: any) => !seenIds.has(getProductKey(p)))
    );
    combined = [...combined, ...uniqueFallback];
  }

  if (combined.length < MAX_EXPERIENCES) {
    const furtherAwayProducts = getCategoryProducts(FURTHER_AWAY_CATEGORY, data);
    const seenIds = new Set(combined.map(getProductKey));
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const qualifiedFurtherAway = sortByRating(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      furtherAwayProducts.filter((p: any) => {
        const rating = parseFloat(p.rating ?? p.ratingValue ?? '0') || 0;
        return rating >= FURTHER_AWAY_MIN_RATING && !seenIds.has(getProductKey(p));
      })
    );
    const needed = MAX_EXPERIENCES - combined.length;
    combined = [...combined, ...qualifiedFurtherAway.slice(0, needed)];
  }

  // If no named category produced results, fall back to any available products
  if (combined.length === 0) {
    combined = sortByRating(getAllAvailableProducts(data));
  }

  return combined.slice(0, MAX_EXPERIENCES);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getAllAvailableProducts = (data: any[]): any[] => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const categorized = data.filter((item: any) => Array.isArray(item?.products));
  if (categorized.length > 0) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return categorized.flatMap((item: any) => item.products);
  }

  // If data looks like a flat list of product cards, use directly
  const looksLikeProducts = data.every(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (item: any) =>
      typeof item?.name === 'string' || typeof item?.description === 'string' || typeof item?.imageUrl === 'string'
  );
  if (looksLikeProducts) {
    return data;
  }

  return [];
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const sortByRating = (products: any[]): any[] => {
  return [...products].sort((a, b) => {
    const ratingA = parseFloat(a.rating ?? a.ratingValue ?? '0') || 0;
    const ratingB = parseFloat(b.rating ?? b.ratingValue ?? '0') || 0;
    return ratingB - ratingA;
  });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getCategoryProducts = (categoryName: string, data: any[]): any[] => {
  const normalizedLookup = categoryName.trim().toLowerCase();

  const category = data.find(
    (item: { name?: string }) => typeof item?.name === 'string' && item.name.trim().toLowerCase() === normalizedLookup
  );

  const fallbackCategory =
    category ??
    data.find((item: { name?: string }) => {
      if (typeof item?.name !== 'string') {
        return false;
      }
      const normalizedName = item.name.trim().toLowerCase();
      return normalizedName.includes(normalizedLookup) || normalizedLookup.includes(normalizedName);
    });

  if (fallbackCategory && Array.isArray(fallbackCategory.products)) {
    return fallbackCategory.products;
  }

  return [];
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const normalizeMBTAResponse = (data: any): any[] => {
  if (Array.isArray(data)) {
    return data;
  }

  if (typeof data === 'string') {
    try {
      const parsedData = JSON.parse(data);
      return normalizeMBTAResponse(parsedData);
    } catch (error) {
      console.error('Unable to parse MBTA response payload', error);
      return [];
    }
  }

  if (Array.isArray(data?.data)) {
    return data.data;
  }

  if (Array.isArray(data?.results)) {
    return data.results;
  }

  if (Array.isArray(data?.products)) {
    return data.products;
  }

  return [];
};
